import { useEffect, useRef, useState } from "react";
import { customToast } from "../../Toast";

interface IframeModalProps {
  open: boolean;
  onClose: () => void;
  iframeSrc: string;
  token: string;
  onStatusUpdate?: (status: string) => void;
  onAccountOpeningResult?: (result: {
    success: boolean;
    accountNumber?: string;
    status: string;
    error?: string;
  }) => void;
}

const heightMap = {
  full: "h-screen",
  large: "h-[90vh]",
  medium: "h-[80vh]",
};
export const IframeModal: React.FC<IframeModalProps> = ({
  open,
  onClose,
  iframeSrc,
  token,
  onStatusUpdate,
  onAccountOpeningResult,
}) => {
  const [loading, setLoading] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const height = "large";

  const targetOrigin = (() => {
    try {
      return iframeSrc ? new URL(iframeSrc).origin : "*";
    } catch {
      return "*";
    }
  })();

  useEffect(() => {
    if (iframeSrc) {
      setLoading(true);
    }
  }, [iframeSrc]);

  const dispatchAuthToken = () => {
    try {
      const userSessionString = localStorage.getItem("sootchy_user_session");

      if (!userSessionString || !token) {
        customToast("Unable to retrieve user session or token", "error");
        return "";
      }

      const message = {
        type: "AUTH_TOKEN",
        payload: {
          userSessionString,
          accessToken: token,
        },
      };

      if (iframeRef.current && iframeRef.current.contentWindow) {
        iframeRef.current.contentWindow.postMessage(message, targetOrigin);
      }

      return token;
    } catch {
      return "";
    }
  };

  const handleIframeLoaded = () => {
    setLoading(false);
    dispatchAuthToken();
  };

  // Listen for messages from the iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (targetOrigin !== "*" && event.origin !== targetOrigin) return;
      const { type, payload } = event.data;

      if (type === "CLOSE_AUTH_TOKEN") {
        onClose();
      } else if (type === "STATUS_UPDATE") {
        onStatusUpdate?.(payload?.status);
      } else if (type === "ACCOUNT_OPENING_RESULT") {
        onAccountOpeningResult?.(payload);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [onAccountOpeningResult, onClose, onStatusUpdate, targetOrigin]);

  if (!open) return null;

  return (
    <div className="fixed inset-0  flex items-center justify-center z-[9999]  bg-black/40 p-4 sm:p-6">
      <div className="absolute inset-0" onClick={onClose} />

      <div
        className={`relative w-full max-w-[1200px] ${heightMap[height]} rounded-lg relative bg-white `}
      >
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white z-[100]">
            <div className="w-10 h-10 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
        <iframe
          ref={iframeRef}
          src={iframeSrc}
          onLoad={() => handleIframeLoaded()}
          onError={() => {
            customToast("Unable to load iframe", "error");
          }}
          className="
            w-full
            h-full
            bg-transparent
          "
          title="Embedded Modal"
        />
      </div>
    </div>
  );
};
